import { PencilSimpleIcon } from "@phosphor-icons/react";
import { useState } from "react";

import useLeadListVariablesMutations from "../../../../datahooks/useLeadListVariablesMutations";

interface EditVariableValueProps {
  initialValue: string;
  listId: string;
  //the lead the value belongs to
  targetUrn: string;
  //named around React's reserved "key" prop
  variableKey: string;
}

export default function EditVariableValue({
  initialValue,
  listId,
  targetUrn,
  variableKey,
}: EditVariableValueProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);

  const { updateVariableValue } = useLeadListVariablesMutations();

  function saveValue(newValue: string) {
    const trimmedValue = newValue.trim();

    setIsEditing(false);

    if (trimmedValue === value) {
      return;
    }

    setValue(trimmedValue);

    updateVariableValue({
      listId,
      targetUrn,
      key: variableKey,
      value: trimmedValue,
    }).catch(() => {
      //the mutation toasts the failure, the cell goes back to the stored value
      setValue(value);
    });
  }

  let mainContent = (
    <button
      type="button"
      aria-label={`Edit value: ${value || "empty"}`}
      className="group/variable-cell flex w-full items-center text-left cursor-text px-2.5"
      onDoubleClick={() => setIsEditing(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          setIsEditing(true);
        }
      }}
    >
      <span className="truncate">{value || "-"}</span>

      <PencilSimpleIcon
        weight="fill"
        className="size-4 opacity-0 absolute right-6 transition-opacity group-hover/variable-cell:opacity-100 text-black-950"
      />
    </button>
  );

  if (isEditing) {
    mainContent = (
      <input
        // biome-ignore lint/a11y/noAutofocus: autofocus is needed here
        autoFocus
        className="h-full px-2.25 bg-white text-black-950 w-full text-button-xs border rounded focus:border-purple-400 focus:outline-none"
        placeholder="Variable"
        //uncontrolled so Escape can drop the draft without a state to revert to
        defaultValue={value}
        onBlur={({ target }) => saveValue(target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            saveValue(e.currentTarget.value);
          } else if (e.key === "Escape") {
            setIsEditing(false);
          }
        }}
      />
    );
  }

  return <div className="flex h-full grow relative">{mainContent}</div>;
}
